import { useState, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useResizeDetector } from "react-resize-detector";
import { useToastNotification, useProfile, useAuthActions } from "@hooks";
import {
  useUpdateAvatarMutation,
  useUpdateColorMutation,
} from "@store/api/auth";
import { updateProfileData } from "@store/profileSlice";
import {
  Avatar,
  Tabs,
  ProfileForm,
  ProfileAvatarSettings,
  ProfileColorSettings,
} from "@components";
import {
  Flex,
  Image,
  Button,
  Icon,
  Text,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  useDisclosure,
  DarkMode,
  Box,
} from "@chakra-ui/react";
import editProfileIcon from "@assets/svg/icon-edit.svg";
import { HiOutlineMail, HiOutlineRefresh } from "react-icons/hi";

const EditProfile = () => {
  const dispatch = useDispatch();
  const { height: avatarSectionHeight, ref: avatarSectionRef } =
    useResizeDetector();
  const profile = useSelector((state) => state.profile.data);

  const { avatar, color } = profile;

  const [avatarPreview, setAvatarPreview] = useState(avatar);
  const [colorSrc, setColorSrc] = useState(color);
  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [settingsTabIndex, setSettingsTabIndex] = useState(0);

  const [updateAvatar, { isLoading: updatingAvatar }] =
    useUpdateAvatarMutation();
  const [updateColor, { isLoading: updatingColor }] = useUpdateColorMutation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { showErrorToast, showSuccessToast } = useToastNotification();

  const { isEmailVerified, userEmail } = useProfile();
  const { resendVerifyEmailForLocalStrategy } = useAuthActions();
  const { resendVerificationEmail, isLoading: isResendingVerification } =
    resendVerifyEmailForLocalStrategy;

  const handleResendVerification = () => {
    if (userEmail) resendVerificationEmail({ email: userEmail });
  };

  const profileTabs = [{ id: 0, label: "Profile", content: <ProfileForm /> }];

  const settingsTab = [
    {
      id: 0,
      label: "Avatar",
      content: (
        <ProfileAvatarSettings
          color={color}
          avatarPreview={avatarPreview}
          setAvatarPreview={setAvatarPreview}
          setIsSaveDisabled={setIsSaveDisabled}
        />
      ),
    },
    {
      id: 1,
      label: "Color",
      content: (
        <ProfileColorSettings
          colorSrc={colorSrc}
          setColorSrc={setColorSrc}
          setIsSaveDisabled={setIsSaveDisabled}
        />
      ),
    },
  ];

  const handleUpdateAvatar = useCallback(async () => {
    try {
      const updatedAvatar = avatarPreview === null ? "" : avatarPreview;
      const result = await updateAvatar({ avatar: updatedAvatar }).unwrap();
      dispatch(updateProfileData(result.user));
      showSuccessToast("Updated avatar successfully");
    } catch (error) {
      showErrorToast("Error updating avatar, please try again.");
    } finally {
      setIsSaveDisabled(true);
    }
  }, [
    avatarPreview,
    updateAvatar,
    dispatch,
    updateProfileData,
    showErrorToast,
    showSuccessToast,
  ]);

  const handleUpdateColor = useCallback(async () => {
    try {
      const result = await updateColor({ color: colorSrc }).unwrap();
      setColorSrc(result.user.color);
      dispatch(updateProfileData(result.user));
      showSuccessToast("Updated profile color successfully");
    } catch (error) {
      showErrorToast("Error updating profile color, please try again.");
    } finally {
      setIsSaveDisabled(true);
    }
  }, [
    colorSrc,
    updateColor,
    setColorSrc,
    dispatch,
    updateProfileData,
    showErrorToast,
    showSuccessToast,
  ]);

  const handleSave = useCallback(
    () => (settingsTabIndex === 0 ? handleUpdateAvatar() : handleUpdateColor()),
    [settingsTabIndex, handleUpdateAvatar, handleUpdateColor]
  );

  const handleCloseModal = () => {
    onClose();
    setAvatarPreview(avatar);
  };

  const tabHeight = `calc(100% - 80px - ${avatarSectionHeight}px)`;

  return (
    <>
      <Flex
        w="full"
        align="center"
        justify="space-between"
        ref={avatarSectionRef}
      >
        <Avatar
          color={profile.color}
          avatar={profile.avatar}
          size="xl"
          rounded={true}
          edit={true}
          onEdit={onOpen}
        />
        <DarkMode>
          <Button
            variant="secondary"
            rightIcon={<Image boxSize="15px" src={editProfileIcon} />}
            onClick={onOpen}
          >
            Edit Avatar
          </Button>
        </DarkMode>
      </Flex>
      <Box w="full">
        <Text
          fontSize="xs"
          fontWeight="700"
          letterSpacing="0.08em"
          color="gray.400"
          mb="1.5"
        >
          EMAIL
        </Text>
        <Flex align="center" gap="2" flexWrap="wrap">
          <Tooltip
            hasArrow
            shouldWrapChildren
            placement="bottom-start"
            label="Email cannot be changed"
          >
            <Flex
              alignItems="center"
              borderRadius="6px"
              bg="rgba(0, 195, 255, 0.20)"
              p="1"
            >
              <Icon as={HiOutlineMail} color="blue.600" boxSize="4" mr="1.5" />
              <Text
                fontSize="sm"
                isTruncated
                maxW="150px"
                color="blue.500"
                pointerEvents="none"
              >
                {profile.email}
              </Text>
            </Flex>
          </Tooltip>
          {!isEmailVerified && (
            <Flex
              align="center"
              gap="1.5"
              bg="yellow.100"
              borderRadius="full"
              px="2.5"
              py="1"
              data-testid="email-unverified-badge"
            >
              <Box boxSize="6px" borderRadius="full" bg="yellow.500" />
              <Text fontSize="xs" fontWeight="600" color="yellow.800">
                Unverified
              </Text>
            </Flex>
          )}
        </Flex>
        {!isEmailVerified && (
          <Button
            variant="link"
            size="sm"
            mt="2"
            color="purple.400"
            fontWeight="700"
            leftIcon={<Icon as={HiOutlineRefresh} boxSize="4" />}
            onClick={handleResendVerification}
            isLoading={isResendingVerification}
            loadingText="Sending..."
            data-testid="resend-verification-email"
          >
            Resend verification link
          </Button>
        )}
      </Box>
      <Box height={tabHeight}>
        <Tabs
          tabs={profileTabs}
          variant="profile"
          tabsProps={{ height: "100%" }}
          tabPanelProps={{ height: "100%" }}
          tabPanelWrapperProps={{ height: "calc(100% - 60px)" }}
        />
      </Box>
      <Modal isOpen={isOpen} onClose={handleCloseModal} size="xs">
        <ModalOverlay />
        <ModalContent bg="dark.500" borderRadius="16px">
          <ModalCloseButton bg="dark.700" color="gray.400" size="sm" />
          <ModalBody>
            <Tabs
              index={settingsTabIndex}
              tabs={settingsTab}
              onChange={(index) => setSettingsTabIndex(index)}
              variant="profile"
            />
          </ModalBody>
          <ModalFooter>
            <Button
              variant="primary"
              isFullWidth
              isDisabled={isSaveDisabled}
              onClick={handleSave}
              isLoading={updatingAvatar || updatingColor}
              loadingText="Saving..."
            >
              Save
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default EditProfile;
